import {useFieldArray, useFormContext} from 'react-hook-form';
import {SVG} from '../../../../../../constants/assetLists';
import AppButton from '../../../../../../components/common/AppButton';
import {
  createDefaultBranch,
  NetworkDetailsFormValues,
  SelectedTreeNode,
} from '../../types';
import {
  canDeleteNodeWithoutContentGuard,
  getShowRemoveButton,
} from '../../hooks/useNetworkTreeGuards';
import BranchTreeRow from './BranchTreeRow';
import TreeDeleteConfirmationDialog from './TreeDeleteConfirmationDialog';
import {canDeleteNode} from '../networkReferenceUtils';
import {useSnackbar} from '../../../../../../context/SnackbarContextProvider';
import {useState} from 'react';
import {nodeSetupCompleteSchema} from '../../networkDetailsSchema';

interface NetworkTreeRowProps {
  networkIndex: number;
  totalNetworkCount: number;
  expandedSet: Set<string>;
  setExpandedSet: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleExpand: (id: string) => void;
  onRemove: () => void;
  selectedNode: SelectedTreeNode | null;
  onSelectNode: (node: SelectedTreeNode) => void;
}

function NetworkTreeRow({
  networkIndex,
  totalNetworkCount,
  expandedSet,
  setExpandedSet,
  toggleExpand,
  onRemove,
  selectedNode,
  onSelectNode,
}: NetworkTreeRowProps) {
  const {showSnackbar} = useSnackbar();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {control, watch} = useFormContext<NetworkDetailsFormValues>();

  const {append: appendBranch, remove: removeBranch} = useFieldArray({
    control,
    name: `network.${networkIndex}.branches`,
    keyName: 'id',
  });

  const node = watch(`network.${networkIndex}`);
  const branches = node?.branches ?? [];

  if (!node) return null;

  const validationResult = nodeSetupCompleteSchema.safeParse(node);
  const isNodeValid = validationResult.success;

  const depth = 0;
  const paddingInline = Math.min(12 + depth * 8, 56);
  const hasChildren = branches.length > 0;
  const expanded = expandedSet.has(node.id);
  const caretIcon = expanded ? SVG.expandIcon : SVG.lessThanIcon;
  const networkBranchCount = branches.length;

  const showRemoveButton = getShowRemoveButton({
    isNetwork: true,
    isBranch: false,
    totalNetworkCount,
    networkBranchCount,
  });

  const handleRemoveNode = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmRemove = () => {
    if (canDeleteNodeWithoutContentGuard(node.id)) {
      onRemove();
      setShowDeleteDialog(false);
      return;
    }

    const result = canDeleteNode(node);

    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      setShowDeleteDialog(false);
      return;
    }

    onRemove();
    setShowDeleteDialog(false);
  };

  const addBranchNode = (e: React.MouseEvent) => {
    e.stopPropagation();
    appendBranch(createDefaultBranch());

    onSelectNode({
      level: 'branch',
      networkIndex: networkIndex,
      branchIndex: branches?.length,
    });
    setExpandedSet(prev => {
      const id = node.id;
      const next = new Set(prev);
      if (next.has(id)) return next;
      else next.add(id);
      return next;
    });
  };

  const isSelected =
    selectedNode?.level === 'network' &&
    selectedNode.networkIndex === networkIndex;

  return (
    <div className="border-b border-gray70 py-1">
      <TreeDeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmRemove}
      />
      <div
        role="button"
        tabIndex={0}
        className={`w-full flex justify-between items-center gap-2 py-2 rounded-lg transition-colors duration-200 hover:bg-pastelBlue group cursor-pointer ${
          isSelected ? 'bg-pastelBlue' : ''
        }`}
        style={{
          paddingInlineStart: `${paddingInline}px`,
          paddingInlineEnd: '8px',
        }}
        onClick={() => onSelectNode({level: 'network', networkIndex})}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            onSelectNode({level: 'network', networkIndex});
          }
        }}>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            {hasChildren && (
              <button
                type="button"
                className="w-[12px] h-[12px]"
                onClick={e => {
                  e.stopPropagation();
                  toggleExpand(node.id);
                }}>
                <img
                  src={caretIcon}
                  alt="caret"
                  className="w-full h-full object-contain"
                />
              </button>
            )}
            <div className="w-[20px] h-[20px]">
              <img
                src={SVG.networkBlackIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`${!isNodeValid ? 'text-raspberryRed' : 'text-black'}`}>
              {node?.name || 'שם הרשת'}
            </span>
            {!isNodeValid && (
              <span
                className="w-[14px] h-[14px] rounded-full bg-raspberryRed shrink-0"
                aria-label="שדה חובה חסר"
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <AppButton
            iconStart={
              <img src={SVG.addIcon} className="w-3 h-3 object-contain" />
            }
            onClick={addBranchNode}>
            סניף
          </AppButton>

          {showRemoveButton && (
            <button
              type="button"
              className="w-3 h-3 hidden group-hover:block"
              onClick={handleRemoveNode}>
              <img
                src={SVG.closeDarkIcon}
                className="w-full h-full object-contain"
                alt="remove"
              />
            </button>
          )}
        </div>
      </div>

      {hasChildren && expanded && (
        <div className="mt-1 flex flex-col gap-1">
          {branches.map((branch, branchIndex) => (
            <BranchTreeRow
              key={branch.id}
              networkIndex={networkIndex}
              branchIndex={branchIndex}
              totalNetworkCount={totalNetworkCount}
              networkBranchCount={networkBranchCount}
              expandedSet={expandedSet}
              setExpandedSet={setExpandedSet}
              toggleExpand={toggleExpand}
              onRemove={() => removeBranch(branchIndex)}
              selectedNode={selectedNode}
              onSelectNode={onSelectNode}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default NetworkTreeRow;
